/* corex-textil-disenos-model.js · «Mis diseños» del personalizador de camisetas: reglas puras (sin DOM).
   - El diseño guardado es el JSON del carrito (CorexTextilCart.designDoc): vistas con sus impresiones ya formadas, en cm.
     itemsFromDoc lo devuelve a los elementos sueltos del editor (delante/detrás: cm de prenda desde el cuello y el eje;
     mangas: cm de la impresión de 10×10 y su distancia al bajo).
   - lostZones: zonas con diseño que la prenda de destino no tiene (se avisa antes de abrirla).
   - fitView: al cargar en otra prenda o talla, cada diseño se reduce (sin deformar) hasta el máximo de la talla de
     referencia y se mueve dentro del límite útil; devuelve avisos de lo que se ha ajustado.
   Las funciones de geometría llegan inyectadas (deps): corners (CorexTextilGeo.layerCorners), derive (impresiones de
   delante/detrás, CorexTextilGroups.derivePrints), scaleLayers (CorexTextilSizing), clampShift (CorexTextilAlign). */
(function (global) {
  'use strict';

  var FLAT_AREA_H = 50;            /* alto del área libre (40×50): dist = centro − 25 en derivePrints */
  var SLEEVE_SIZE = '10x10';
  var EPS = 0.05;                  /* cm: lo que no se nota no se avisa */
  var THUMB_TRANSFORM = 'c_fit,w_64,h_64/';

  function round(v, d) { var f = Math.pow(10, d == null ? 2 : d); return Math.round(v * f) / f; }
  function fmt(v) { return String(Math.round(v * 10) / 10).replace('.', ','); }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  /* Nombre propuesto: "<producto> · dd/mm/aaaa". */
  function defaultName(titulo, date) {
    var d = date || new Date();
    return String(titulo || 'Diseño').trim() + ' · ' + pad2(d.getDate()) + '/' + pad2(d.getMonth() + 1) + '/' + d.getFullYear();
  }

  /* Miniatura de una capa con imagen de Cloudinary (64 px), para la lista de capas del editor. */
  function thumbOf(src) {
    return /^https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\//.test(src || '') ? src.replace('/image/upload/', '/image/upload/' + THUMB_TRANSFORM) : src;
  }
  function isSleevePrint(p) { return p && p.size === SLEEVE_SIZE; }
  /* ¿Tiene la vista algo que imprimir? */
  function viewHasDesign(v) {
    return !!(v && (v.prints || []).some(function (p) { return (p.layers || []).length > 0; }));
  }
  function designZones(doc) {
    var vistas = (doc && doc.vistas) || {};
    return Object.keys(vistas).filter(function (id) { return viewHasDesign(vistas[id]); });
  }
  /* Zonas con diseño que no existen en la prenda de destino (ids de zona). */
  function lostZones(doc, targetZoneIds) {
    return designZones(doc).filter(function (id) { return (targetZoneIds || []).indexOf(id) === -1; });
  }

  /* Elementos del editor por vista: { vista: { items, dist } } (dist solo en mangas). */
  function itemsFromDoc(doc) {
    var vistas = (doc && doc.vistas) || {}, out = {};
    Object.keys(vistas).forEach(function (id) {
      var prints = (vistas[id].prints || []).filter(function (p) { return (p.layers || []).length; });
      if (!prints.length) return;
      if (isSleevePrint(prints[0])) {
        out[id] = { items: prints[0].layers.map(cleanLayer), dist: prints[0].dist == null ? null : prints[0].dist };
        return;
      }
      out[id] = { items: prints.reduce(function (acc, p) {
        var cx = Number(p.dx) || 0, cy = (Number(p.dist) || 0) + FLAT_AREA_H / 2;
        return acc.concat(p.layers.map(function (l) { return Object.assign(cleanLayer(l), { x: round(l.x + cx, 3), y: round(l.y + cy, 3) }); }));
      }, []), dist: null };
    });
    return out;
  }
  function cleanLayer(l) {
    var c = Object.assign({}, l);
    delete c.itemIds;
    if (c.type === 'image') c.thumb = thumbOf(c.src);
    return c;
  }

  /* Talla de referencia en la prenda de destino: la misma si existe; si no, la del medio. */
  function pickRefSize(ref, sizes) {
    if (!sizes || !sizes.length) return { talla: null, cambiada: false };
    if (sizes.indexOf(ref) !== -1) return { talla: ref, cambiada: false };
    return { talla: sizes[Math.floor((sizes.length - 1) / 2)], cambiada: !!ref };
  }

  /* Mayor factor ≤ 1 para que w×h quepa en limit { w, h, film: 'AxB' } (film en cualquier orientación). */
  function fitFactor(w, h, limit) {
    if (!(w > 0) || !(h > 0)) return 1;
    var f = 1;
    if (isFinite(limit.w)) f = Math.min(f, limit.w / w);
    if (isFinite(limit.h)) f = Math.min(f, limit.h / h);
    if (limit.film) {
      var s = String(limit.film).split('x').map(Number);
      f = Math.min(f, Math.max(Math.min(s[0] / w, s[1] / h), Math.min(s[1] / w, s[0] / h)));
    }
    return Math.max(0, f);
  }

  function boxOfItems(items, ids, corners) {
    var pts = items.filter(function (l) { return !l.hidden && (!ids || ids.indexOf(l.id) !== -1); })
      .reduce(function (acc, l) { return acc.concat(corners(l)); }, []);
    if (!pts.length) return null;
    var xs = pts.map(function (p) { return p.x; }), ys = pts.map(function (p) { return p.y; });
    return { x0: Math.min.apply(null, xs), x1: Math.max.apply(null, xs), y0: Math.min.apply(null, ys), y1: Math.max.apply(null, ys) };
  }
  function shiftItems(items, ids, dx, dy) {
    return items.map(function (l) {
      return ids.indexOf(l.id) === -1 ? l : Object.assign({}, l, { x: round(l.x + dx, 3), y: round(l.y + dy, 3) });
    });
  }
  /* Encaja un grupo de elementos: dentro del límite útil, al máximo de la talla (ancla arriba o abajo) y otra vez dentro. */
  function fitGroup(items, ids, o, deps, avisos, name) {
    var move = function (list) {
      var b = boxOfItems(list, ids, deps.corners);
      if (!b) return { items: list, moved: false };
      var s = deps.clampShift(b, o.bounds);
      var moved = Math.abs(s.dx) > EPS || Math.abs(s.dy) > EPS;
      return { items: moved ? shiftItems(list, ids, s.dx, s.dy) : list, moved: moved };
    };
    var first = move(items), b = boxOfItems(first.items, ids, deps.corners);
    if (!b) return items;
    var w = b.x1 - b.x0, h = b.y1 - b.y0, f = fitFactor(w, h, o.limit(o.anchor === 'top' ? b.y0 : 0));
    var list = first.items;
    if (f < 1 - 1e-6) {
      list = deps.scaleLayers(list, ids, f, deps.corners, o.anchor).map(function (l) {
        if (ids.indexOf(l.id) === -1) return l;
        var p = l.type === 'text' ? { size: Math.floor(l.size * 100) / 100 } : { w: Math.floor(l.w * 100) / 100 };
        return Object.assign({}, l, p, { x: round(l.x, 3), y: round(l.y, 3) });
      });
      /* el redondeo hacia abajo encoge un poco: se vuelve a apoyar en el ancla (la distancia al cuello o al bajo no cambia) */
      var rb = boxOfItems(list, ids, deps.corners);
      list = shiftItems(list, ids, 0, o.anchor === 'top' ? b.y0 - rb.y0 : b.y1 - rb.y1);
      var nb = boxOfItems(list, ids, deps.corners);
      avisos.push(name + ': se ha reducido de ' + fmt(w) + ' × ' + fmt(h) + ' a ' + fmt(nb.x1 - nb.x0) + ' × ' + fmt(nb.y1 - nb.y0) + ' cm para no pasar del máximo de la talla ' + o.talla + '.');
    }
    var second = move(list);
    if (first.moved || second.moved) avisos.push(name + ': se ha movido para que no pase del límite útil de la prenda.');
    return second.items;
  }

  /* Encaja los elementos de una vista en la prenda de destino.
     o: { label, kind ('front'|'back'|'sleeve'), items, talla, limit(top) → { w, h, film } (máximo en la talla),
          bounds: { x0, x1, y0, y1 } (límite útil para mover) }. Devuelve { items, avisos }. */
  function fitView(o, deps) {
    var avisos = [], items = o.items.slice();
    if (o.kind === 'sleeve') {
      items = fitGroup(items, items.map(function (l) { return l.id; }), { limit: o.limit, bounds: o.bounds, anchor: 'bottom', talla: o.talla }, deps, avisos, o.label);
      return { items: items, avisos: avisos };
    }
    /* delante/detrás: cada impresión (grupo de elementos a menos de 2 cm) por separado */
    deps.derive(items).forEach(function (p, i, all) {
      var name = o.label + (all.length > 1 ? ' (diseño ' + (i + 1) + ')' : '');
      items = fitGroup(items, p.itemIds, { limit: o.limit, bounds: o.bounds, anchor: 'top', talla: o.talla }, deps, avisos, name);
    });
    return { items: items, avisos: avisos };
  }

  /* Texto de zonas para avisos: ["Manga izq.", "Manga dcha."] → "Manga izq. y Manga dcha." */
  function joinList(list) {
    if (list.length <= 1) return list.join('');
    return list.slice(0, -1).join(', ') + ' y ' + list[list.length - 1];
  }

  var api = {
    FLAT_AREA_H: FLAT_AREA_H, defaultName: defaultName, thumbOf: thumbOf, designZones: designZones, lostZones: lostZones,
    itemsFromDoc: itemsFromDoc, pickRefSize: pickRefSize, fitFactor: fitFactor, fitView: fitView, joinList: joinList
  };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else global.CorexTextilDisenosModel = api;
})(this);
